import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  AlertCircle, ArrowLeft, Building2, Clock, KeyRound, Loader2, LockKeyhole, Mail, MessageCircle,
  ShieldCheck, Smartphone
} from 'lucide-react';
import { toast } from 'sonner';
import { portalFetch, setPortalSession, PORTAL_OFF_MESSAGE } from '../../lib/clientPortalApi';
import PortalBrandPanel from '../../components/portal/PortalBrandPanel';

// DCS Client Portal — sign in (client-login-v2, 7 Sep 2026).
// Address: /portal/client  (/portal/login still lands here via a <Navigate replace> in App.js).
//
// Ruhshad, 6 Sep: "THE LOGO IS DIFFERENT, WHY? … KEEP THE LOGIN BOX ON TOP AND MOVE AND SPREAD THE
// TEXT BELOW AND GIVE EVEN MORE CONTENT … THE COMPANIES THAT ARE TIED UP WITH US THE BANNER …
// ALLOW THE CLIENTS TO LOGIN USING WHATSAPP OTP, GIVE THEM A CHOICE."
//
// Formation: DCS header -> the sign-in card centred on top (full width on a phone, ~480px on desktop)
// -> the brand / benefit content spread out underneath (PortalBrandPanel variant="below") -> the
// "Companies that trust DCS" banner -> footer.
//
// The logo: the public homepage (PortalHomePage.js, the "/" route) shows settings.company_logo_url
// from GET /api/public/settings, with /images/daruwallas-logo.png as its fallback. The earlier client
// page hardcoded the staff sidebar's Emergent-hosted "LLP Logo" asset instead — a different file.
// This page now does exactly what the homepage does, so both show the same mark.
//
// Three ways in (a segmented control on the card):
//   password      POST /auth/login                          (unchanged; respects must_change_password)
//   email_otp     POST /auth/otp/request {email}  -> /auth/otp/verify {email, code}
//   whatsapp_otp  POST /auth/otp/request {phone}  -> /auth/otp/verify {phone, code}
//                 GET /auth/otp/methods says whether WhatsApp is switched on yet; while it is not
//                 (409 from the server, or methods.whatsapp_otp.available false) the tab shows
//                 "WhatsApp sign-in is being switched on" instead of a form.
// Other modes kept from the earlier page: forgot (email -> 6-digit code + new password) and change
// (forced first-login password change). A 503 anywhere means the portal is switched off in Settings.

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HOME_LOGO_FALLBACK = '/images/daruwallas-logo.png';
const CARE_EMAIL = 'care@daruwallas.com';
const SITE_URL = 'https://daruwallas.com';
const COMPANY_NAME = 'Daruwalla Consultancy Services';
const ADDRESS = 'Daruwalla Consultancy Services LLP · Fort, Mumbai';
const WA_SWITCHING_ON = 'WhatsApp sign-in is being switched on. Please use your email for now.';

const METHODS = [
  { key: 'password', label: 'Password', icon: LockKeyhole, testid: 'cp-tab-password' },
  { key: 'email_otp', label: 'Email code', icon: Mail, testid: 'cp-tab-email-otp' },
  { key: 'whatsapp_otp', label: 'WhatsApp code', icon: MessageCircle, testid: 'cp-tab-whatsapp-otp' }
];

const HEADINGS = {
  login: { title: 'Sign in to your client portal', subtitle: 'Choose how you would like to sign in today.' },
  forgot: { title: 'Reset your password', subtitle: 'We will email a 6-digit code to your work address.' },
  change: { title: 'Set your own password', subtitle: 'One last step before your requirements open up.' }
};

const avatarFor = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Client')}&background=f5f5f5&color=333&size=200&font-size=0.28`;

// ---------------------------------------------------------------------------- chrome
function BrandHeader({ logoUrl }) {
  return (
    <header
      className="py-4 sm:py-5 px-4 border-b bg-white/85 backdrop-blur-sm sticky top-0 z-10"
      data-testid="cp-brand-header"
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <img
            src={logoUrl || HOME_LOGO_FALLBACK}
            alt={COMPANY_NAME}
            className="h-10 sm:h-12 md:h-14 w-auto object-contain"
            onError={(e) => { e.target.onerror = null; e.target.src = HOME_LOGO_FALLBACK; }}
            data-testid="cp-logo"
          />
          <div className="hidden sm:block h-8 w-px bg-slate-200" aria-hidden="true" />
          <div className="text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-[#a9862f] font-bold whitespace-nowrap">
            Client Portal
          </div>
        </div>
        <a
          href={`mailto:${CARE_EMAIL}`}
          className="hidden sm:inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 whitespace-nowrap"
        >
          <Mail className="w-4 h-4" /> {CARE_EMAIL}
        </a>
      </div>
    </header>
  );
}

function BrandFooter() {
  return (
    <footer className="py-8 sm:py-10 px-4 border-t bg-white/60 mt-12 sm:mt-20" data-testid="cp-footer">
      <div className="max-w-6xl mx-auto text-center text-xs sm:text-sm text-slate-500">
        <p className="font-medium text-slate-700">{ADDRESS}</p>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-2">
          <a href={`mailto:${CARE_EMAIL}`} className="hover:text-slate-800">{CARE_EMAIL}</a>
          <span className="hidden sm:inline text-slate-300">·</span>
          <a href={SITE_URL} target="_blank" rel="noopener noreferrer" className="hover:text-slate-800">daruwallas.com</a>
        </div>
        <p className="mt-3">© {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.</p>
        <p className="mt-3 text-xs text-slate-400 max-w-2xl mx-auto">
          This portal is for HR, talent acquisition and hiring teams at DCS client companies. Candidate information
          shown here is confidential and shared for your hiring process only.
        </p>
      </div>
    </footer>
  );
}

function TroubleLine() {
  return (
    <p className="text-center text-xs sm:text-sm text-slate-500 mt-5" data-testid="cp-trouble-line">
      Having trouble?{' '}
      <a href={`mailto:${CARE_EMAIL}`} className="font-medium text-slate-700 underline underline-offset-2 hover:text-slate-900">
        Write to {CARE_EMAIL}
      </a>{' '}
      and your consultant will get you in.
    </p>
  );
}

// "Companies that trust DCS" — the same list the public homepage's COMPANIES THAT HIRE THROUGH US
// marquee shows: GET /api/public/stats -> approved_clients [{name, logo_url}] (clients ticked
// show_on_homepage in the ATS). Hidden when the list is empty, exactly as the homepage hides it.
function TrustedCompanies({ clients }) {
  if (!Array.isArray(clients) || clients.length === 0) return null;
  const loop = clients.length >= 4 ? [...clients, ...clients] : clients;
  const animate = clients.length >= 4;
  return (
    <section className="mt-14 sm:mt-20" data-testid="cp-trusted-companies">
      <p className="text-[11px] uppercase font-bold tracking-[0.28em] text-center text-[#a9862f]">
        Companies that trust DCS
      </p>
      <h2 className="mt-3 text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 text-center">
        Hiring teams we already work with
      </h2>
      <p className="mt-3 text-sm sm:text-base text-slate-500 text-center max-w-2xl mx-auto">
        From shipping and manufacturing to IT and finance, these companies trust DCS to find their next hire —
        and their HR teams use this portal.
      </p>
      <style>{`
        @keyframes cp-marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .cp-marquee { animation: cp-marquee 40s linear infinite; }
        .cp-marquee:hover { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .cp-marquee { animation: none; flex-wrap: wrap; justify-content: center; } }
      `}</style>
      <div className="relative mt-8 overflow-hidden">
        {animate && (
          <>
            <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-r from-slate-50 to-transparent z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-l from-slate-50 to-transparent z-10" />
          </>
        )}
        <div className={`flex gap-4 sm:gap-6 ${animate ? 'cp-marquee w-max' : 'flex-wrap justify-center'}`}>
          {loop.map((client, idx) => (
            <div
              key={`${client.name}-${idx}`}
              className="w-[200px] h-[96px] sm:w-[240px] sm:h-[120px] px-5 py-3 rounded-2xl bg-white flex items-center justify-center shadow-md border border-slate-100 shrink-0"
              title={client.name}
              data-testid="cp-trusted-company"
            >
              <img
                src={client.logo_url || avatarFor(client.name)}
                alt={client.name}
                className="max-h-[64px] sm:max-h-[84px] max-w-[170px] sm:max-w-[200px] object-contain"
                loading="lazy"
                onError={(e) => { e.target.onerror = null; e.target.src = avatarFor(client.name); }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ErrorBox({ text, failCount, showHint }) {
  if (!text) return null;
  return (
    <div
      className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800"
      role="alert"
      data-testid="cp-form-error"
    >
      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <span>
        {text}
        {failCount > 0 && (
          <span className="text-red-700" data-testid="cp-fail-count">
            {' '}({failCount} failed {failCount === 1 ? 'attempt' : 'attempts'})
          </span>
        )}
        {showHint && (
          <span className="block mt-1 text-xs text-red-700">
            Three tries gone. Use “Forgot your password?”, switch to a one-time code, or write to {CARE_EMAIL}.
          </span>
        )}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------- the page
export default function ClientLoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');           // login | forgot | change
  const [method, setMethod] = useState('password');    // password | email_otp | whatsapp_otp
  const [otpStep, setOtpStep] = useState('enter');     // enter | code   (for the two OTP methods)
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [busy, setBusy] = useState(false);
  const [portalOff, setPortalOff] = useState(false);
  const [codeSent, setCodeSent] = useState(false);     // forgot-password flow
  const [formError, setFormError] = useState(null);
  const [failCount, setFailCount] = useState(0);
  const [methods, setMethods] = useState(null);        // GET /auth/otp/methods
  const [logoUrl, setLogoUrl] = useState(null);        // GET /api/public/settings -> company_logo_url
  const [trusted, setTrusted] = useState([]);          // GET /api/public/stats -> approved_clients

  // public bits the homepage also loads: logo + the tied-up companies
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/public/settings`);
        const d = r && r.ok ? await r.json() : null;
        if (alive && d && d.company_logo_url) setLogoUrl(d.company_logo_url);
      } catch { /* fallback asset */ }
      try {
        const r = await fetch(`${API_URL}/api/public/stats`);
        const d = r && r.ok ? await r.json() : null;
        if (alive && d && Array.isArray(d.approved_clients)) setTrusted(d.approved_clients.slice(0, 50));
      } catch { /* section hides itself */ }
    })();
    return () => { alive = false; };
  }, []);

  // which sign-in methods are switched on (also the earliest place a 503 shows up)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const m = await portalFetch('/auth/otp/methods', { auth: false });
        if (alive) setMethods(m);
      } catch (err) {
        if (!alive) return;
        if (err && err.status === 503) setPortalOff(true);
        else setMethods(null);                          // module not installed yet: tabs still show, server decides
      }
    })();
    return () => { alive = false; };
  }, []);

  const whatsappAvailable = !methods || !methods.whatsapp_otp || methods.whatsapp_otp.available !== false;
  const whatsappReason = (methods && methods.whatsapp_otp && methods.whatsapp_otp.reason) || WA_SWITCHING_ON;

  const fail = (err) => {
    if (err && err.status === 503) { setPortalOff(true); return; }
    const detail = (err && err.detail) || 'Something went wrong';
    setFormError(detail);
    toast.error(detail);
  };

  const finishSignIn = (data) => {
    setPortalSession(data.access_token, data.user, data.client);
    setFailCount(0);
    if (data.must_change_password) {
      setMode('change');
      toast.info('Please set your own password to continue.');
    } else {
      navigate('/portal/home', { replace: true });
    }
  };

  // --- password ------------------------------------------------------------------------------
  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      const data = await portalFetch('/auth/login', { method: 'POST', body: { email, password }, auth: false });
      finishSignIn(data);
    } catch (err) {
      if (err && err.status === 401) setFailCount((n) => n + 1);
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  // --- one-time codes (email / WhatsApp) --------------------------------------------------------
  const otpBody = useCallback(() => (method === 'whatsapp_otp' ? { phone } : { email }), [method, phone, email]);

  const requestOtp = async (e) => {
    if (e) e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      await portalFetch('/auth/otp/request', { method: 'POST', body: otpBody(), auth: false });
      setOtpStep('code');
      setCode('');
      toast.success(method === 'whatsapp_otp'
        ? 'If that number has portal access, a 6-digit code is on its way on WhatsApp.'
        : 'If that address has portal access, a 6-digit code is on its way.');
    } catch (err) {
      if (err && err.status === 409 && method === 'whatsapp_otp') {
        setMethods((m) => ({ ...(m || {}), whatsapp_otp: { available: false, reason: err.detail || WA_SWITCHING_ON } }));
        setFormError(err.detail || WA_SWITCHING_ON);
        return;
      }
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      const data = await portalFetch('/auth/otp/verify', { method: 'POST', body: { ...otpBody(), code }, auth: false });
      finishSignIn(data);
    } catch (err) {
      if (err && (err.status === 400 || err.status === 429)) setFailCount((n) => n + 1);
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  // --- forgot / reset / forced change (unchanged flows) ----------------------------------------
  const sendCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      await portalFetch('/auth/forgot', { method: 'POST', body: { email }, auth: false });
      setCodeSent(true);
      toast.success('If that address has portal access, a 6-digit code is on its way.');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (newPassword !== newPassword2) { setFormError('Passwords do not match'); toast.error('Passwords do not match'); return; }
    setBusy(true);
    setFormError(null);
    try {
      await portalFetch('/auth/reset', { method: 'POST', body: { email, code, new_password: newPassword }, auth: false });
      toast.success('Password set. Please sign in.');
      setMode('login');
      setMethod('password');
      setPassword('');
      setCode('');
      setNewPassword('');
      setNewPassword2('');
      setCodeSent(false);
      setFailCount(0);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const doChange = async (e) => {
    e.preventDefault();
    if (newPassword !== newPassword2) { setFormError('Passwords do not match'); toast.error('Passwords do not match'); return; }
    setBusy(true);
    setFormError(null);
    try {
      await portalFetch('/auth/change-password', { method: 'POST', body: { new_password: newPassword } });
      toast.success('Password updated.');
      navigate('/portal/home', { replace: true });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const switchMethod = (key) => {
    setMethod(key);
    setOtpStep('enter');
    setCode('');
    setFormError(null);
    setFailCount(0);
  };

  const backToLogin = () => {
    setMode('login');
    setCodeSent(false);
    setFormError(null);
  };

  const heading = HEADINGS[mode] || HEADINGS.login;
  const fieldCls = 'pl-10 h-11 sm:h-12 max-md:h-[46px] text-base max-md:text-[16px]';

  const emailField = (id, testid, extra = {}) => (
    <div>
      <Label htmlFor={id} className="text-slate-700">Work email</Label>
      <div className="relative mt-1.5">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" placeholder="you@yourcompany.com" className={fieldCls} data-testid={testid} {...extra} />
      </div>
    </div>
  );

  const codeField = (id, testid, hint) => (
    <div>
      <Label htmlFor={id} className="text-slate-700">6-digit code</Label>
      <Input id={id} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required autoFocus placeholder="123456" className="mt-1.5 h-12 sm:h-14 max-md:h-[48px] tracking-[0.45em] text-center font-mono text-xl" data-testid={testid} />
      <p className="text-xs text-slate-400 mt-1.5">{hint}</p>
    </div>
  );

  return (
    <div
      className="min-h-screen flex flex-col bg-gradient-to-b from-slate-50 via-white to-slate-50"
      data-testid="client-login-page"
    >
      <BrandHeader logoUrl={logoUrl} />

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 py-8 sm:py-12 lg:py-16">
        {/* ---- the box, centred on top ---- */}
        <div className="w-full max-w-[480px] mx-auto" data-testid="cp-card-wrap">
          {portalOff ? (
            <Card className="shadow-xl sm:shadow-2xl border-0 overflow-hidden" data-testid="portal-off-card">
              <div className="h-1.5 bg-amber-400" />
              <CardContent className="p-6 sm:p-8 text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-7 h-7 sm:w-8 sm:h-8 text-amber-600" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-slate-800 mb-2">The client portal is not open yet</h2>
                <p className="text-sm text-slate-600" data-testid="portal-off-banner">
                  {PORTAL_OFF_MESSAGE}. Please contact {CARE_EMAIL}.
                </p>
                <p className="text-sm text-slate-500 mt-4">
                  We are switching it on client by client. Until then your DCS consultant will keep sending
                  requirements, candidate dossiers and agreements by email exactly as before.
                </p>
                <div className="mt-6 pt-5 border-t border-slate-100">
                  <a href={`mailto:${CARE_EMAIL}`} className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900">
                    <Mail className="w-4 h-4" /> {CARE_EMAIL}
                  </a>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="shadow-xl sm:shadow-2xl border-0 overflow-hidden">
                <div className="h-1.5 bg-gradient-to-r from-[#c9a84c] via-[#e5d5a1] to-[#c9a84c]" />
                <CardContent className="p-5 sm:p-7 md:p-8">
                  <div className="mb-5 sm:mb-6 text-center">
                    <p className="text-[11px] tracking-[0.28em] uppercase text-[#a9862f] font-bold">DCS Client Portal</p>
                    <h1 className="text-xl sm:text-2xl font-bold text-slate-800 mt-1.5">{heading.title}</h1>
                    <p className="text-xs sm:text-sm text-slate-500 mt-1.5">{heading.subtitle}</p>
                  </div>

                  {/* ---- the three ways in ---- */}
                  {mode === 'login' && (
                    <div
                      role="tablist"
                      aria-label="Sign-in method"
                      className="mb-5 grid grid-cols-3 gap-1 rounded-xl bg-slate-100 p-1"
                      data-testid="cp-method-tabs"
                    >
                      {METHODS.map(({ key, label, icon: Icon, testid }) => {
                        const active = method === key;
                        const dim = key === 'whatsapp_otp' && !whatsappAvailable;
                        return (
                          <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            onClick={() => switchMethod(key)}
                            className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 rounded-lg px-1 py-2 text-[11px] sm:text-xs font-semibold transition-colors ${
                              active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
                            } ${dim ? 'opacity-70' : ''}`}
                            data-testid={testid}
                            data-active={active ? 'true' : 'false'}
                          >
                            <Icon className={`w-4 h-4 ${key === 'whatsapp_otp' && !dim ? 'text-emerald-600' : ''}`} />
                            <span className="leading-none">{label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <ErrorBox
                    text={formError}
                    failCount={mode === 'login' ? failCount : 0}
                    showHint={mode === 'login' && method === 'password' && failCount >= 3}
                  />

                  {/* (a) email + password */}
                  {mode === 'login' && method === 'password' && (
                    <form onSubmit={doLogin} className="space-y-4" data-testid="cp-form-password">
                      {emailField('cp-email', 'cp-email')}
                      <div>
                        <Label htmlFor="cp-password" className="text-slate-700">Password</Label>
                        <div className="relative mt-1.5">
                          <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input id="cp-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" placeholder="••••••••" className={fieldCls} data-testid="cp-password" />
                        </div>
                      </div>
                      <Button type="submit" className="w-full h-11 sm:h-12 max-md:h-[48px] bg-slate-800 hover:bg-slate-700 text-base" disabled={busy} data-testid="cp-login-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LockKeyhole className="w-4 h-4 mr-2" />}
                        Sign in
                      </Button>
                      <button type="button" className="w-full text-sm text-slate-500 hover:text-slate-800" onClick={() => { setMode('forgot'); setFormError(null); }} data-testid="cp-forgot-link">
                        Forgot your password?
                      </button>
                    </form>
                  )}

                  {/* (b) email one-time code */}
                  {mode === 'login' && method === 'email_otp' && (
                    <form onSubmit={otpStep === 'code' ? verifyOtp : requestOtp} className="space-y-4" data-testid="cp-form-email-otp">
                      <p className="text-sm text-slate-600">
                        No password needed. We email a 6-digit code to your work address; type it here and you are in.
                      </p>
                      {emailField('cp-otp-email', 'cp-otp-email', { disabled: otpStep === 'code' })}
                      {otpStep === 'code' && codeField('cp-otp-code', 'cp-otp-code', 'The code expires in 10 minutes. Check your spam folder if it has not arrived.')}
                      <Button type="submit" className="w-full h-11 sm:h-12 max-md:h-[48px] bg-slate-800 hover:bg-slate-700 text-base" disabled={busy} data-testid="cp-otp-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : (otpStep === 'code' ? <KeyRound className="w-4 h-4 mr-2" /> : <Mail className="w-4 h-4 mr-2" />)}
                        {otpStep === 'code' ? 'Sign in with this code' : 'Email me a code'}
                      </Button>
                      {otpStep === 'code' && (
                        <div className="flex items-center justify-between text-sm text-slate-500">
                          <button type="button" className="inline-flex items-center gap-1 hover:text-slate-800" onClick={() => { setOtpStep('enter'); setCode(''); setFormError(null); }} data-testid="cp-otp-change-target">
                            <ArrowLeft className="w-3.5 h-3.5" /> Use a different email
                          </button>
                          <button type="button" className="hover:text-slate-800" onClick={requestOtp} disabled={busy} data-testid="cp-otp-resend">
                            Resend code
                          </button>
                        </div>
                      )}
                    </form>
                  )}

                  {/* (c) WhatsApp one-time code */}
                  {mode === 'login' && method === 'whatsapp_otp' && !whatsappAvailable && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 sm:p-5 text-sm text-amber-900" data-testid="cp-whatsapp-off">
                      <div className="flex items-start gap-3">
                        <MessageCircle className="w-5 h-5 mt-0.5 shrink-0 text-amber-600" />
                        <div>
                          <p className="font-semibold">WhatsApp sign-in is being switched on</p>
                          <p className="mt-1 text-amber-800">{whatsappReason}</p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button type="button" className="text-sm font-medium underline underline-offset-2" onClick={() => switchMethod('email_otp')} data-testid="cp-whatsapp-off-email">
                              Get a code by email instead
                            </button>
                            <span className="text-amber-300">·</span>
                            <button type="button" className="text-sm font-medium underline underline-offset-2" onClick={() => switchMethod('password')} data-testid="cp-whatsapp-off-password">
                              Use my password
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {mode === 'login' && method === 'whatsapp_otp' && whatsappAvailable && (
                    <form onSubmit={otpStep === 'code' ? verifyOtp : requestOtp} className="space-y-4" data-testid="cp-form-whatsapp-otp">
                      <p className="text-sm text-slate-600">
                        We send a 6-digit code to the WhatsApp number your DCS consultant has on file for you.
                      </p>
                      <div>
                        <Label htmlFor="cp-otp-phone" className="text-slate-700">Mobile number (WhatsApp)</Label>
                        <div className="relative mt-1.5">
                          <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input id="cp-otp-phone" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required disabled={otpStep === 'code'} autoComplete="tel" placeholder="+91 98200 12345" className={fieldCls} data-testid="cp-otp-phone" />
                        </div>
                        {otpStep !== 'code' && <p className="text-xs text-slate-400 mt-1.5">Indian numbers can be typed with or without +91.</p>}
                      </div>
                      {otpStep === 'code' && codeField('cp-otp-code', 'cp-otp-code', 'The code expires in 10 minutes. Open WhatsApp and tap “Copy code”.')}
                      <Button type="submit" className="w-full h-11 sm:h-12 max-md:h-[48px] bg-emerald-700 hover:bg-emerald-600 text-base" disabled={busy} data-testid="cp-otp-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : (otpStep === 'code' ? <KeyRound className="w-4 h-4 mr-2" /> : <MessageCircle className="w-4 h-4 mr-2" />)}
                        {otpStep === 'code' ? 'Sign in with this code' : 'Send me a WhatsApp code'}
                      </Button>
                      {otpStep === 'code' && (
                        <div className="flex items-center justify-between text-sm text-slate-500">
                          <button type="button" className="inline-flex items-center gap-1 hover:text-slate-800" onClick={() => { setOtpStep('enter'); setCode(''); setFormError(null); }} data-testid="cp-otp-change-target">
                            <ArrowLeft className="w-3.5 h-3.5" /> Use a different number
                          </button>
                          <button type="button" className="hover:text-slate-800" onClick={requestOtp} disabled={busy} data-testid="cp-otp-resend">
                            Resend code
                          </button>
                        </div>
                      )}
                    </form>
                  )}

                  {/* forgot password (unchanged flow) */}
                  {mode === 'forgot' && (
                    <form onSubmit={codeSent ? doReset : sendCode} className="space-y-4">
                      {emailField('cp-femail', 'cp-forgot-email', { disabled: codeSent })}
                      {codeSent && (
                        <>
                          <div>
                            <Label htmlFor="cp-code" className="text-slate-700">6-digit code (from your email)</Label>
                            <Input id="cp-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required placeholder="123456" className="mt-1.5 h-12 max-md:h-[48px] tracking-[0.45em] text-center font-mono text-xl" data-testid="cp-otp" />
                            <p className="text-xs text-slate-400 mt-1.5">The code expires in 10 minutes. Check your spam folder if it has not arrived.</p>
                          </div>
                          <div>
                            <Label htmlFor="cp-np" className="text-slate-700">New password (min 8 characters)</Label>
                            <Input id="cp-np" type="password" minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required autoComplete="new-password" className="mt-1.5 h-11 sm:h-12 max-md:h-[46px] max-md:text-[16px]" data-testid="cp-new-password" />
                          </div>
                          <div>
                            <Label htmlFor="cp-np2" className="text-slate-700">Repeat new password</Label>
                            <Input id="cp-np2" type="password" minLength={8} value={newPassword2} onChange={(e) => setNewPassword2(e.target.value)} required autoComplete="new-password" className="mt-1.5 h-11 sm:h-12 max-md:h-[46px] max-md:text-[16px]" data-testid="cp-new-password-2" />
                          </div>
                        </>
                      )}
                      <Button type="submit" className="w-full h-11 sm:h-12 max-md:h-[48px] bg-slate-800 hover:bg-slate-700 text-base" disabled={busy} data-testid="cp-forgot-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : (codeSent ? <ShieldCheck className="w-4 h-4 mr-2" /> : <Mail className="w-4 h-4 mr-2" />)}
                        {codeSent ? 'Set new password' : 'Email me a code'}
                      </Button>
                      <div className="flex items-center justify-between text-sm text-slate-500">
                        <button type="button" className="inline-flex items-center gap-1 hover:text-slate-800" onClick={backToLogin} data-testid="cp-back-to-login">
                          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
                        </button>
                        {codeSent && (
                          <button type="button" className="hover:text-slate-800" onClick={sendCode} disabled={busy} data-testid="cp-resend-code">
                            Resend code
                          </button>
                        )}
                      </div>
                    </form>
                  )}

                  {/* forced first-login change (unchanged flow) */}
                  {mode === 'change' && (
                    <form onSubmit={doChange} className="space-y-4">
                      <p className="text-sm text-slate-600">You signed in with a temporary password. Choose your own to continue.</p>
                      <div>
                        <Label htmlFor="cp-cnp" className="text-slate-700">New password (min 8 characters)</Label>
                        <Input id="cp-cnp" type="password" minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required autoComplete="new-password" className="mt-1.5 h-11 sm:h-12 max-md:h-[46px] max-md:text-[16px]" data-testid="cp-change-password" />
                      </div>
                      <div>
                        <Label htmlFor="cp-cnp2" className="text-slate-700">Repeat new password</Label>
                        <Input id="cp-cnp2" type="password" minLength={8} value={newPassword2} onChange={(e) => setNewPassword2(e.target.value)} required autoComplete="new-password" className="mt-1.5 h-11 sm:h-12 max-md:h-[46px] max-md:text-[16px]" data-testid="cp-change-password-2" />
                      </div>
                      <Button type="submit" className="w-full h-11 sm:h-12 max-md:h-[48px] bg-slate-800 hover:bg-slate-700 text-base" disabled={busy} data-testid="cp-change-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                        Save and continue
                      </Button>
                    </form>
                  )}

                  <TroubleLine />
                </CardContent>
              </Card>

              <p className="text-center text-xs text-slate-400 mt-4 px-2">
                Access is limited to the people your organisation has invited. Ask your DCS consultant to add a
                colleague, or add them yourself from the Team tab once you are in.
              </p>
            </>
          )}
        </div>

        {/* ---- everything else, spread out underneath ---- */}
        <div className="mt-14 sm:mt-20" data-testid="cp-below">
          <PortalBrandPanel variant="below" showLogo={false} />
          <TrustedCompanies clients={trusted} />
          <div className="mt-12 sm:mt-16 flex items-center justify-center gap-2 text-xs sm:text-sm text-slate-500" data-testid="cp-address-line">
            <Building2 className="w-4 h-4 text-[#a9862f]" />
            <span>{ADDRESS} · <a href={`mailto:${CARE_EMAIL}`} className="hover:text-slate-800">{CARE_EMAIL}</a></span>
          </div>
        </div>
      </main>

      <BrandFooter />
    </div>
  );
}
